/**
 * ContactInquiries — 고객센터 문의 관리
 *
 * 좌측: 문의 목록 (상태/카테고리 필터 + 검색 + 페이지네이션)
 * 우측: 선택된 문의 상세 + 회신 이메일 작성 폼
 *   - 회신 발송 시 SES 로 송신 → 자동 RESOLVED 처리 + audit log 기록
 *   - 회신 이력은 상세 패널 하단에 표시
 */

import { useEffect, useMemo, useState } from 'react'
import {
  CONTACT_INQUIRY_CATEGORY_LABELS,
  CONTACT_INQUIRY_STATUS_LABELS,
  type ContactInquiryCategory,
  type ContactInquiryStatus,
  type ReplyContactInquiryRequest,
} from '@fiket/shared'
import { apiFetch } from '@/lib/api'
import { useApiFetch } from '../hooks/useApiFetch'
import { useToast } from '../components/ToastContainer'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Textarea } from '../ui/Textarea'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { Pagination } from '../ui/Pagination'
import { AdminTable } from '../ui/AdminTable'
import type { TableColumn } from '../ui/Table'
import s from './ContactInquiries.module.css'

interface InquiryItem {
  id: string
  userId: string | null
  userEmail: string | null
  name: string
  email: string
  category: ContactInquiryCategory
  status: ContactInquiryStatus
  message: string
  notificationSent: boolean
  resolvedAt: string | null
  createdAt: string
}

interface ListResponse {
  items: InquiryItem[]
  counts: Record<ContactInquiryStatus, number>
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

interface InquiryDetail {
  inquiry: InquiryItem & {
    userName: string | null
    userAgent: string | null
    ipHash: string | null
  }
  replies: Array<{
    id: string
    actorId: string | null
    actorEmail: string | null
    status: string
    createdAt: string
    metadata: {
      to?: string
      from?: string
      messageId?: string
      subject?: string
      body?: string
      error?: string
    } | null
  }>
}

const STATUS_BADGE_COLOR: Record<ContactInquiryStatus, 'red' | 'yellow' | 'green'> = {
  UNREAD: 'red',
  READ: 'yellow',
  RESOLVED: 'green',
}

const STATUS_OPTIONS: Array<{ value: '' | ContactInquiryStatus; label: string }> = [
  { value: '', label: '전체' },
  { value: 'UNREAD', label: '미확인' },
  { value: 'READ', label: '확인' },
  { value: 'RESOLVED', label: '답변완료' },
]

const CATEGORY_OPTIONS: Array<{ value: '' | ContactInquiryCategory; label: string }> = [
  { value: '', label: '전체' },
  { value: 'BUG', label: '버그/오류' },
  { value: 'PAYMENT', label: '결제' },
  { value: 'ACCOUNT', label: '계정' },
  { value: 'FEATURE', label: '기능 제안' },
  { value: 'OTHER', label: '기타' },
]

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })
}

function defaultSubject(category: ContactInquiryCategory) {
  return `[wedding.fiket] 회신 - ${CONTACT_INQUIRY_CATEGORY_LABELS[category]} 문의`
}

function defaultBody(name: string) {
  return [
    `안녕하세요 ${name}님,`,
    '',
    'wedding.fiket 입니다. 문의주셔서 감사합니다.',
    '',
    '',
    '',
    '— wedding.fiket 고객지원팀',
  ].join('\n')
}

export function ContactInquiries() {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<'' | ContactInquiryStatus>('')
  const [categoryFilter, setCategoryFilter] = useState<'' | ContactInquiryCategory>('')
  const [q, setQ] = useState('')
  const [qInput, setQInput] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [refreshTick, setRefreshTick] = useState(0)

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('pageSize', String(pageSize))
    if (statusFilter) p.set('status', statusFilter)
    if (categoryFilter) p.set('category', categoryFilter)
    if (q) p.set('q', q)
    p.set('_t', String(refreshTick))
    return p.toString()
  }, [page, pageSize, statusFilter, categoryFilter, q, refreshTick])

  const { data, isLoading } = useApiFetch<ListResponse>(
    `/api/admin/contact-inquiries?${queryString}`,
  )

  const detailPath = selectedId
    ? `/api/admin/contact-inquiries/${selectedId}?_t=${refreshTick}`
    : null
  const { data: detail, isLoading: detailLoading } = useApiFetch<InquiryDetail>(detailPath)

  // 선택된 문의가 UNREAD 면 자동으로 READ 처리
  useEffect(() => {
    const inq = detail?.inquiry
    if (!inq || inq.status !== 'UNREAD') return
    void (async () => {
      try {
        await apiFetch(`/api/admin/contact-inquiries/${inq.id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'READ' }),
        })
        setRefreshTick((t) => t + 1)
      } catch {
        // 실패해도 조용히 — 다음 액션에서 다시 시도됨
      }
    })()
  }, [detail?.inquiry?.id, detail?.inquiry?.status])

  // 페이지/필터 변경 시 선택 초기화
  useEffect(() => {
    setSelectedId(null)
  }, [page, statusFilter, categoryFilter, q])

  const items = data?.items ?? []
  const pagination = data?.pagination
  const counts = data?.counts

  const columns: TableColumn<InquiryItem>[] = [
    {
      key: 'createdAt',
      label: '접수',
      width: '140px',
      render: (row) => fmtDate(row.createdAt),
    },
    {
      key: 'status',
      label: '상태',
      width: '90px',
      render: (row) => (
        <Badge color={STATUS_BADGE_COLOR[row.status]}>
          {CONTACT_INQUIRY_STATUS_LABELS[row.status]}
        </Badge>
      ),
    },
    {
      key: 'category',
      label: '유형',
      width: '90px',
      render: (row) => CONTACT_INQUIRY_CATEGORY_LABELS[row.category],
    },
    {
      key: 'name',
      label: '문의자',
      render: (row) => (
        <span>
          <strong>{row.name}</strong>
          <span style={{ color: 'var(--ds-color-text-secondary)', marginLeft: 6 }}>
            {row.email}
          </span>
        </span>
      ),
    },
    {
      key: 'message',
      label: '내용',
      render: (row) => (
        <span
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            color: 'var(--ds-color-text-secondary)',
            fontSize: 12,
          }}
        >
          {row.message}
        </span>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 80px)' }}>
      {/* 좌측 — 리스트 */}
      <div style={{ flex: '1 1 55%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>고객 문의</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--ds-color-text-secondary)', fontSize: 13 }}>
            {counts && (
              <>
                미확인 <strong>{counts.UNREAD}</strong> · 확인 <strong>{counts.READ}</strong> ·
                답변완료 <strong>{counts.RESOLVED}</strong>
              </>
            )}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 140px' }}>
            <span style={{ fontSize: 12, color: 'var(--ds-color-text-secondary)' }}>상태</span>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setPage(1)
                setStatusFilter(e.target.value as '' | ContactInquiryStatus)
              }}
              options={STATUS_OPTIONS}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 140px' }}>
            <span style={{ fontSize: 12, color: 'var(--ds-color-text-secondary)' }}>유형</span>
            <Select
              value={categoryFilter}
              onChange={(e) => {
                setPage(1)
                setCategoryFilter(e.target.value as '' | ContactInquiryCategory)
              }}
              options={CATEGORY_OPTIONS}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 auto' }}>
            <span style={{ fontSize: 12, color: 'var(--ds-color-text-secondary)' }}>
              검색 (이름/이메일/내용)
            </span>
            <Input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1)
                  setQ(qInput.trim())
                }
              }}
              placeholder="Enter 키로 검색"
            />
          </label>
        </div>

        <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
          <AdminTable
            data={items}
            columns={columns}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            emptyMessage="문의가 없습니다"
            onRowClick={(row) => setSelectedId(row.id)}
            hoverable
          />
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div style={{ paddingTop: 12 }}>
            <Pagination
              current={pagination.page}
              total={pagination.totalPages}
              onChange={setPage}
            />
          </div>
        )}
      </div>

      {/* 우측 — 상세 + 회신 */}
      <aside style={{ flex: '1 1 45%', minWidth: 420, display: 'flex', flexDirection: 'column' }}>
        {!selectedId && (
          <div className={s.panel}>
            <div className={s.emptyState}>문의를 선택하세요.</div>
          </div>
        )}
        {selectedId && (!detail || detailLoading) && (
          <div className={s.panel}>
            <div className={s.emptyState}>
              <Spinner />
            </div>
          </div>
        )}
        {detail && (
          <InquiryDetailPanel
            detail={detail}
            onChanged={() => setRefreshTick((t) => t + 1)}
          />
        )}
      </aside>
    </div>
  )
}

function InquiryDetailPanel({
  detail,
  onChanged,
}: {
  detail: InquiryDetail
  onChanged: () => void
}) {
  const { inquiry, replies } = detail
  const { addToast } = useToast()

  const [subject, setSubject] = useState(() => defaultSubject(inquiry.category))
  const [body, setBody] = useState(() => defaultBody(inquiry.name))
  const [sending, setSending] = useState(false)

  // 선택된 문의가 바뀌면 폼 초기화
  useEffect(() => {
    setSubject(defaultSubject(inquiry.category))
    setBody(defaultBody(inquiry.name))
  }, [inquiry.id, inquiry.category, inquiry.name])

  const sendReply = async () => {
    if (sending) return
    if (subject.trim().length === 0 || body.trim().length === 0) {
      addToast({ type: 'error', message: '제목과 내용을 모두 입력하세요.' })
      return
    }
    setSending(true)
    try {
      await apiFetch<{ id: string; sentAt: string }>(
        `/api/admin/contact-inquiries/${inquiry.id}/reply`,
        {
          method: 'POST',
          body: JSON.stringify({ subject, body } satisfies ReplyContactInquiryRequest),
        },
      )
      addToast({ type: 'success', message: `${inquiry.email} 로 회신 발송 완료` })
      onChanged()
    } catch (e) {
      addToast({
        type: 'error',
        message: e instanceof Error ? e.message : '회신 발송 실패',
      })
    } finally {
      setSending(false)
    }
  }

  const updateStatus = async (next: ContactInquiryStatus) => {
    try {
      await apiFetch(`/api/admin/contact-inquiries/${inquiry.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      })
      addToast({
        type: 'success',
        message: `상태를 ${CONTACT_INQUIRY_STATUS_LABELS[next]}(으)로 변경했습니다.`,
      })
      onChanged()
    } catch (e) {
      addToast({
        type: 'error',
        message: e instanceof Error ? e.message : '상태 변경 실패',
      })
    }
  }

  return (
    <div className={s.panel}>
      <header className={s.panelHeader}>
        <div className={s.headerInfo}>
          <h3 className={s.headerTitle}>
            {inquiry.name} · {inquiry.email}
          </h3>
          <div className={s.headerMeta}>
            <Badge color={STATUS_BADGE_COLOR[inquiry.status]}>
              {CONTACT_INQUIRY_STATUS_LABELS[inquiry.status]}
            </Badge>
            <span>{CONTACT_INQUIRY_CATEGORY_LABELS[inquiry.category]}</span>
            <span>· 접수 {fmtDate(inquiry.createdAt)}</span>
            {inquiry.resolvedAt && <span>· 해결 {fmtDate(inquiry.resolvedAt)}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {inquiry.status !== 'RESOLVED' && (
            <Button size="sm" variant="ghost" onClick={() => updateStatus('RESOLVED')}>
              답변완료로 표시
            </Button>
          )}
          {inquiry.status === 'RESOLVED' && (
            <Button size="sm" variant="ghost" onClick={() => updateStatus('READ')}>
              미해결로 되돌리기
            </Button>
          )}
        </div>
      </header>

      <div className={s.body}>
        <div className={s.section}>
          <div className={s.sectionLabel}>문의 내용</div>
          <div className={s.message}>{inquiry.message}</div>
        </div>

        <div className={s.section}>
          <div className={s.sectionLabel}>메타 정보</div>
          <div className={s.metaGrid}>
            <div className={s.metaKey}>회원</div>
            <div className={s.metaVal}>
              {inquiry.userId
                ? `${inquiry.userEmail ?? '(no email)'} (${inquiry.userId})`
                : '비로그인'}
            </div>
            <div className={s.metaKey}>접수 ID</div>
            <div className={s.metaVal} style={{ fontFamily: 'monospace', fontSize: 11 }}>
              {inquiry.id}
            </div>
            <div className={s.metaKey}>Slack 알림</div>
            <div className={s.metaVal}>{inquiry.notificationSent ? '발송됨' : '미발송'}</div>
            <div className={s.metaKey}>User-Agent</div>
            <div className={s.metaVal} style={{ fontSize: 11 }}>
              {inquiry.userAgent ?? '—'}
            </div>
          </div>
        </div>

        {replies.length > 0 && (
          <div className={s.section}>
            <div className={s.sectionLabel}>회신 이력 ({replies.length}건)</div>
            <div className={s.replyHistory}>
              {replies.map((r) => (
                <div key={r.id} className={s.replyItem}>
                  <div className={s.replyItemHeader}>
                    <span>
                      {r.actorEmail ?? r.actorId ?? '(unknown)'} · {fmtDate(r.createdAt)}
                    </span>
                    <Badge color={r.status === 'SUCCESS' ? 'green' : 'red'}>{r.status}</Badge>
                  </div>
                  {r.metadata?.from && (
                    <div style={{ fontSize: 11, color: 'var(--ds-color-text-tertiary)' }}>
                      {r.metadata.from} → {r.metadata.to}
                    </div>
                  )}
                  {r.metadata?.subject && (
                    <div className={s.replyItemSubject}>{r.metadata.subject}</div>
                  )}
                  {r.metadata?.body && (
                    <div className={s.replyItemBody}>{r.metadata.body}</div>
                  )}
                  {r.metadata?.error && (
                    <div className={s.replyItemBody} style={{ color: 'var(--ds-color-danger)' }}>
                      {r.metadata.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={s.replyForm}>
        <div className={s.replyHeader}>
          <span>회신 작성</span>
          <span className={s.replyHint}>
            발송 시 {inquiry.email} 로 전송되며 자동으로 답변완료 처리됩니다.
          </span>
        </div>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="제목"
        />
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          placeholder="회신 내용"
        />
        <div className={s.replyActions}>
          <Button onClick={sendReply} disabled={sending}>
            {sending ? '발송 중...' : '회신 이메일 발송'}
          </Button>
        </div>
      </div>
    </div>
  )
}
